/**
 * Parsing timestamps that came from somewhere else.
 *
 * `new Date(iso)` cannot be trusted with our data, because JavaScript resolves
 * a missing timezone in two opposite directions depending on the shape of the
 * string:
 *
 *   new Date('2026-07-29T20:42:33')  → 8:42 PM LOCAL   (offsetless date-time)
 *   new Date('2026-07-29')           → midnight UTC     (offsetless date)
 *
 * Both shapes come out of this API, and both were wrong. Session timestamps are
 * stored as UTC and were serialised without a 'Z', so every time in the app read
 * exactly the device's own UTC offset late — 4 hours in Toronto, 3 on a phone an
 * hour east, 5 in January. Day strings from the grouped-stats endpoints went the
 * other way: parsed as UTC midnight, they render as *yesterday* anywhere west of
 * Greenwich for most of the day.
 *
 * The server now sends 'Z' on every instant, but this stays: rows already sitting
 * in IndexedDB were written by the old build, and a phone that hasn't re-pulled
 * still holds them. Parse defensively at the edge and the display is right on
 * every device regardless of which build wrote the row.
 */

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/
const HAS_ZONE = /(?:Z|[+-]\d{2}:?\d{2})$/i

/**
 * A Date from any timestamp shape this app handles.
 *
 * A bare date becomes local noon rather than local midnight — the day it names
 * is then the day it renders as, in every zone, with twelve hours of slack
 * against a DST shift moving it over a boundary.
 */
export function parseIso(iso: string): Date {
  if (DATE_ONLY.test(iso)) return new Date(`${iso}T12:00:00`)
  if (HAS_ZONE.test(iso)) return new Date(iso)
  return new Date(`${iso}Z`)          // naive == UTC, per the storage convention
}

/** Milliseconds since the epoch, for arithmetic that doesn't need a Date. */
export function isoMs(iso: string): number {
  return parseIso(iso).getTime()
}

/** e.g. "6:42 PM" */
export function formatClock(iso: string): string {
  return parseIso(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
}
